import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    Avatar,
    IconButton,
    Collapse,
    Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PaymentIcon from '@mui/icons-material/Payment';
import dataProvider from './dataProvider';
import { API_URL } from './apiConfig';

const PROVIDER_CONFIGS = {
    'twilio': {
        name: 'Twilio SMS',
        description: 'Send SMS notifications to guests using Twilio.',
        logo: <Avatar sx={{ bgcolor: '#F22F46', width: 48, height: 48 }}><PhoneIcon sx={{ fontSize: 24 }} /></Avatar>,
        fields: [
            { name: 'Account SID', key: 'client_id', type: 'text' },
            { name: 'Auth Token', key: 'client_secret', type: 'password' },
            { name: 'From Number', key: 'from_number', type: 'text' },
        ]
    },
    'sendgrid': {
        name: 'SendGrid Email',
        description: 'Send email notifications using SendGrid.',
        logo: <Avatar sx={{ bgcolor: '#1A73E8', width: 48, height: 48 }}><EmailIcon sx={{ fontSize: 24 }} /></Avatar>,
        fields: [
            { name: 'API Key', key: 'api_key', type: 'password' },
            { name: 'From Email', key: 'from_name', type: 'email' },
            { name: 'Host Email', key: 'api_secret', type: 'email' },
        ]
    },
    'mailersend': {
        name: 'MailerSend Email',
        description: 'Send email notifications using MailerSend.',
        logo: <Avatar sx={{ bgcolor: '#6366F1', width: 48, height: 48 }}><EmailIcon sx={{ fontSize: 24 }} /></Avatar>,
        fields: [
            { name: 'API Token', key: 'api_key', type: 'password' },
            { name: 'From Email', key: 'from_name', type: 'email' },
            { name: 'Host Email', key: 'api_secret', type: 'email' },
        ]
    },
    'stripe': {
        name: 'Stripe Payments',
        description: 'Connect your Stripe account to receive payments from your customers.',
        logo: <Avatar sx={{ bgcolor: '#6772E5', width: 48, height: 48 }}><PaymentIcon sx={{ fontSize: 24 }} /></Avatar>,
        fields: [
            { name: 'Publishable Key', key: 'api_key', type: 'text' },
            { name: 'Secret Key', key: 'api_secret', type: 'password' },
            { name: 'Webhook Secret', key: 'webhook_secret', type: 'password' },
        ]
    }
};

const Integrations = () => {
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({});
    const [formState, setFormState] = useState({});

    // Fetch integrations from backend
    useEffect(() => {
        const fetchIntegrations = async () => {
            try {
                const response = await dataProvider.getList('integrations', {
                    pagination: { page: 1, perPage: 100 },
                    sort: { field: 'id', order: 'ASC' },
                    filter: {}
                });
                setIntegrations(response.data);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching integrations:', error);
                setLoading(false);
            }
        };

        fetchIntegrations();
    }, []);

    const handleChange = (providerId, field, value) => {
        setFormState(prev => ({
            ...prev,
            [providerId]: {
                ...prev[providerId],
                [field]: value
            }
        }));
    };

    const toggleExpand = (providerId) => {
        setExpanded(prev => ({
            ...prev,
            [providerId]: !prev[providerId]
        }));
    };

    const handleSave = async (providerId) => {
        try {
            const token = localStorage.getItem('access_token');
            const integrationData = formState[providerId];
            
            // Check if integration exists
            const existingIntegration = integrations.find(i => i.provider === providerId);
            
            const url = existingIntegration
                ? `${API_URL}/integrations/${existingIntegration.id}/`
                : `${API_URL}/integrations/`;
            
            const method = existingIntegration ? 'PUT' : 'POST';
            
            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(integrationData)
            });
            
            if (response.ok) {
                alert(`Successfully saved ${PROVIDER_CONFIGS[providerId]?.name || providerId} settings!`);
                // Refresh integrations
                window.location.reload();
            } else {
                alert('Error saving integration. Please try again.');
            }
        } catch (error) {
            console.error('Error saving integration:', error);
            alert('Error saving integration. Please try again.');
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <Typography>Loading integrations...</Typography>
            </Box>
        );
    }

    // Get available providers
    const availableProviders = ['twilio', 'stripe', 'sendgrid', 'mailersend'];

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                Integrations
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {availableProviders.map((providerId) => {
                    const providerConfig = PROVIDER_CONFIGS[providerId];
                    const existingIntegration = integrations.find(i => i.provider === providerId);
                    const isExpanded = expanded[providerId] || false;
                    
                    return (
                        <Card
                            key={providerId}
                            sx={{
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                '&:hover': {
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                },
                                transition: 'box-shadow 0.3s ease'
                            }}
                        >
                            <CardContent sx={{ p: 0 }}>
                                {/* Header */}
                                <Box 
                                    sx={{ 
                                        p: 3, 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'space-between',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => toggleExpand(providerId)}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        {providerConfig.logo}
                                        <Box>
                                            <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                                                {providerConfig.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {providerConfig.description}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <IconButton size="small">
                                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Box>

                                {/* Expandable Content */}
                                <Collapse in={isExpanded}>
                                    <Divider />
                                    <Box sx={{ p: 3 }}>
                                        <Box component="form" onSubmit={e => { e.preventDefault(); handleSave(providerId); }}>
                                            <Box sx={{ display: 'grid', gap: 3, mb: 4 }}>
                                                {providerConfig.fields.map((field) => (
                                                    <TextField
                                                        key={field.key}
                                                        label={field.name}
                                                        type={field.type}
                                                        fullWidth
                                                        value={formState[providerId]?.[field.key] || ''}
                                                        onChange={e => handleChange(providerId, field.key, e.target.value)}
                                                        variant="outlined"
                                                        size="medium"
                                                    />
                                                ))}
                                            </Box>

                                            <Button 
                                                type="submit" 
                                                variant="contained" 
                                                color="primary" 
                                                size="large"
                                                sx={{ 
                                                    px: 4, 
                                                    py: 1.5, 
                                                    fontSize: 16,
                                                    fontWeight: 600,
                                                    textTransform: 'none'
                                                }}
                                            >
                                                SAVE {providerConfig.name.toUpperCase()} SETTINGS
                                            </Button>
                                        </Box>
                                    </Box>
                                </Collapse>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>
        </Box>
    );
};

export default Integrations; 